import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import {
  CST_FORM_STATE_OPTIONS,
  CST_FORM_TYPE_OPTIONS,
  CST_STATE,
  CST_TYPE,
  mergeCustomerServiceTypeDetailsWithLanguages,
  stripCustomerServiceTypeDetails,
} from './customerCenterTypeConstants';

const EMPTY_FORM = {
  sort: 1,
  type: CST_TYPE.OFFLINE,
  state: CST_STATE.ON,
  image: '',
  details: [],
};

export default function CustomerCenterTypeEditModal({ open, typeId, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(typeId);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const langList = await adminApi.newsLanguages();
        if (cancelled) {
          return;
        }
        setLanguages(langList || []);
        if (typeId) {
          const data = await adminApi.getCustomerServiceType(typeId);
          if (cancelled) {
            return;
          }
          setForm({
            sort: data.sort ?? 1,
            type: data.type ?? CST_TYPE.OFFLINE,
            state: data.state ?? CST_STATE.ON,
            image: data.image || '',
            details: mergeCustomerServiceTypeDetailsWithLanguages(langList, data.details),
          });
        } else {
          setForm({
            ...EMPTY_FORM,
            details: mergeCustomerServiceTypeDetailsWithLanguages(langList, []),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '載入失敗');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, typeId]);

  function updateDetail(index, value) {
    setForm((prev) => {
      const nextDetails = [...prev.details];
      nextDetails[index] = { ...nextDetails[index], name: value };
      return { ...prev, details: nextDetails };
    });
  }

  async function handleImageUpload(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setUploadingImage(true);
    setError('');
    try {
      const url = await adminApi.uploadOssImage(file);
      setForm((prev) => ({ ...prev, image: url }));
    } catch (err) {
      setError(err.message || '圖片上傳失敗');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  }

  function handleReset() {
    setError('');
    if (isEdit && typeId) {
      setLoading(true);
      Promise.all([adminApi.newsLanguages(), adminApi.getCustomerServiceType(typeId)])
        .then(([langList, data]) => {
          setLanguages(langList || []);
          setForm({
            sort: data.sort ?? 1,
            type: data.type ?? CST_TYPE.OFFLINE,
            state: data.state ?? CST_STATE.ON,
            image: data.image || '',
            details: mergeCustomerServiceTypeDetailsWithLanguages(langList, data.details),
          });
        })
        .catch((err) => setError(err.message || '載入失敗'))
        .finally(() => setLoading(false));
      return;
    }
    setForm({
      ...EMPTY_FORM,
      details: mergeCustomerServiceTypeDetailsWithLanguages(languages, []),
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    if (!form.image?.trim()) {
      setError('請上傳客服種類圖標');
      return;
    }
    const details = stripCustomerServiceTypeDetails(form.details).filter((item) => item.name?.trim());
    if (details.length === 0) {
      setError('至少填寫一個語系名稱');
      return;
    }
    const payload = {
      sort: Number(form.sort) || 1,
      type: Number(form.type),
      state: Number(form.state),
      image: form.image.trim(),
      details,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await adminApi.updateCustomerServiceType({ id: typeId, ...payload });
      } else {
        await adminApi.createCustomerServiceType(payload);
      }
      onSaved?.(isEdit);
      onClose?.();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="confirm-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__header">
          <h3>{isEdit ? '修改' : '新增'}</h3>
          <button type="button" className="btn btn-link" onClick={onClose}>
            ×
          </button>
        </div>

        <form className="modal-panel__body static-content-edit-form" onSubmit={handleSubmit}>
          {error ? <p className="form-message form-message--error">{error}</p> : null}
          {loading ? <p className="table-empty">載入中…</p> : null}

          {!loading ? (
            <>
              <div className="form-grid form-grid--2">
                <div className="form-field">
                  <label htmlFor="cstSort">顯示順序</label>
                  <input
                    id="cstSort"
                    type="number"
                    min="1"
                    value={form.sort}
                    onChange={(e) => setForm((prev) => ({ ...prev, sort: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <fieldset className="form-fieldset">
                <legend>客服種類類型</legend>
                <div className="radio-group">
                  {CST_FORM_TYPE_OPTIONS.map((opt) => (
                    <label key={opt.value} className="radio-group__item">
                      <input
                        type="radio"
                        name="cstType"
                        value={opt.value}
                        checked={Number(form.type) === opt.value}
                        onChange={() => setForm((prev) => ({ ...prev, type: opt.value }))}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </fieldset>

              <fieldset className="form-fieldset">
                <legend>狀態</legend>
                <div className="radio-group">
                  {CST_FORM_STATE_OPTIONS.map((opt) => (
                    <label key={opt.value} className="radio-group__item">
                      <input
                        type="radio"
                        name="cstState"
                        value={opt.value}
                        checked={Number(form.state) === opt.value}
                        onChange={() => setForm((prev) => ({ ...prev, state: opt.value }))}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="form-field">
                <span className="form-field__label">客服種類圖標</span>
                <div className="carousel-image-upload">
                  {form.image ? (
                    <img src={resolveAdminMediaUrl(form.image)} alt="" className="carousel-thumb-preview" />
                  ) : (
                    <div className="carousel-thumb-placeholder">未設定</div>
                  )}
                  <label className="btn btn-success">
                    {uploadingImage ? '上傳中…' : '上傳客服種類 logo 圖（120×120）'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      disabled={uploadingImage || submitting}
                      onChange={handleImageUpload}
                    />
                  </label>
                </div>
              </div>

              {form.details.map((detail, index) => (
                <div key={detail.lang} className="form-field">
                  <label htmlFor={`cstName-${detail.lang}`}>
                    客服種類名稱（{detail._label || detail.lang}）
                  </label>
                  <input
                    id={`cstName-${detail.lang}`}
                    type="text"
                    value={detail.name || ''}
                    onChange={(e) => updateDetail(index, e.target.value)}
                    placeholder={`請輸入${detail._label || detail.lang}名稱`}
                  />
                </div>
              ))}
            </>
          ) : null}

          <div className="modal-panel__footer">
            <button type="button" className="btn btn-secondary" onClick={handleReset} disabled={submitting || loading}>
              重置
            </button>
            <button type="button" className="btn btn-danger-outline" onClick={onClose} disabled={submitting}>
              關閉
            </button>
            <button type="submit" className="btn btn-success" disabled={submitting || loading || uploadingImage}>
              {submitting ? '提交中…' : '提交'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
